import get from 'lodash/get';
import { getExamData } from '../../resources';
import {
  CHANGE_EXAM,
  APPROVE_EXAM,
  FAILURE_EXAM_DATA,
  INVALID_EXAM_ATTRIBUTES,
  RECEIVE_EXAM_DATA,
  REQUEST_EXAM_DATA,
  RESULT_NOT_READY,
  UPDATE_APPEAL_DATA,
  SET_EXAMS_AVAILABILITY
} from '../constants';
import { currentExamSelector, examListSelector } from '../examData/selectors';
import examMapped from '../serializers/exam';
import getAvailableAppealDeadline from '../../services/getAvailableAppealDeadline';
import { findAppeal } from '../appealFind/actions';
import { saveDraft } from '../draft/actions';

export const getExamDataMiddleware = ({
  dispatch,
  getState
}) => next => action => {
  const { type, payload } = action;

  if (type !== REQUEST_EXAM_DATA) {
    return next(action);
  }

  const { saveApprove } = action.payload;

  getExamData(action.payload)
    .then(response => {
      let examList = examMapped(response);

      if (payload.saveApprove) {
        const state = getState();
        const currentExamList = examListSelector(state);

        examList = examList.map(exam => {
          const currentExam = currentExamList.find(
            ({ examId }) => examId === exam.examId
          );

          if (currentExam) {
            return {
              ...exam,
              examApprove: currentExam.examApprove,
              grades_table: [...currentExam.grades_table]
            };
          }

          return exam;
        });
      }

      dispatch({
        type: RECEIVE_EXAM_DATA,
        payload: {
          examList,
          saveApprove
        }
      });
    })
    .catch(error => {
      switch (get(error, 'json.code')) {
        case INVALID_EXAM_ATTRIBUTES:
          return dispatch({ type: INVALID_EXAM_ATTRIBUTES });
        case RESULT_NOT_READY:
          return dispatch({ type: RESULT_NOT_READY });
        default:
          return dispatch({ type: FAILURE_EXAM_DATA, error });
      }
    });

  return next(action);
};

export const updateAppealDataMiddleware = ({
  getState,
  dispatch
}) => next => action => {
  if (action.type !== UPDATE_APPEAL_DATA) {
    return next(action);
  }

  const { checked, comment, changedTask, blur } = action.payload;

  const state = getState();
  const examList = examListSelector(state);
  const currentExam = currentExamSelector(state);
  const changeTaskNumber = changedTask.task_number;

  const existInGradesTable = currentExam.grades_table.some(
    answer => answer.task_number === changeTaskNumber
  );

  const trimmedComment = comment.replace(/(^\s+)/, '');

  const task = {
    ...changedTask,
    checked,
    comment: trimmedComment,
    invalid: !trimmedComment,
    max_mark: changedTask.primary_score,
    mark: changedTask.test_score,
    detailed: changedTask.detailed
  };

  const currentExamGradesTableChanged = [...currentExam.grades_table].map(
    answer => {
      if (changeTaskNumber !== answer.task_number) {
        return {
          ...answer
        };
      }

      return task;
    }
  );

  if (!existInGradesTable) {
    currentExamGradesTableChanged.push(task);
  }

  const payload = examList.map(examOriginal => {
    if (currentExam.examId === examOriginal.examId) {
      return {
        ...currentExam,
        grades_table: currentExamGradesTableChanged
      };
    }
    return { ...examOriginal };
  });

  const nextAction = next({ type: UPDATE_APPEAL_DATA, payload });

  if (blur) {
    dispatch(saveDraft());
  }

  return nextAction;
};

export const approveExamMiddleware = ({ getState }) => next => action => {
  if (action.type !== APPROVE_EXAM) {
    return next(action);
  }

  const state = getState();
  const examList = examListSelector(state);

  const payload = examList.map(exam => {
    if (exam.subject_name === action.payload) {
      return {
        ...exam,
        examApprove: true
      };
    }
    return exam;
  });

  return next({ type: APPROVE_EXAM, payload });
};

export const setExamsAvailabilityMiddleware = ({
  dispatch
}) => next => action => {
  if (action.type !== RECEIVE_EXAM_DATA) {
    return next(action);
  }

  const { payload: { examList = [], saveApprove } = {} } = action;

  const hasAvailableExams = examList.some(
    exam => !!getAvailableAppealDeadline(exam)
  );

  dispatch({
    type: SET_EXAMS_AVAILABILITY,
    payload: hasAvailableExams
  });

  const nextResult = next(action);

  if (!saveApprove) {
    dispatch(saveDraft());
  }

  return nextResult;
};

export const changeExamMiddleware = ({ dispatch }) => next => action => {
  if (action.type !== CHANGE_EXAM) {
    return next(action);
  }

  if (action.payload) {
    dispatch(findAppeal(action.payload));
  }

  return next(action);
};
